import type { BuildingStatistic, BuildingStatisticRoom } from "../../api/generated";

export interface OccupancyTotals {
  totalTables: number;
  occupiedTables: number;
  availableTables: number;
  occupiedByInternTables: number;
  reservedTables: number;
}

export interface OccupancyFloor {
  id?: number;
  name?: string;
  totals: OccupancyTotals;
}

export interface OccupancyBuilding {
  id?: number;
  name?: string;
  city?: string;
  floors: OccupancyFloor[];
  totals: OccupancyTotals;
}

// Only office counts contain every seat category; floor/building counts omit
// interns and reservations. Use the same aggregation at every level.
export const sumOccupancyTotals = (items: BuildingStatisticRoom[]): OccupancyTotals =>
  items.reduce<OccupancyTotals>(
    (total, item) => ({
      totalTables: total.totalTables + (item.totalTables ?? 0),
      occupiedTables: total.occupiedTables + (item.occupiedTables ?? 0),
      availableTables: total.availableTables + (item.availableTables ?? 0),
      occupiedByInternTables: total.occupiedByInternTables + (item.occupiedByInternTables ?? 0),
      reservedTables: total.reservedTables + (item.reservedTables ?? 0),
    }),
    { totalTables: 0, occupiedTables: 0, availableTables: 0, occupiedByInternTables: 0, reservedTables: 0 },
  );

export const getOccupancyOverview = (cities: BuildingStatistic[]): { buildings: OccupancyBuilding[]; totals: OccupancyTotals } => {
  const buildings = cities.flatMap((city) =>
    (city.buildingStatisticBuildings ?? []).map((building) => {
      const floors = (building.buildingStatisticFloors ?? []).map((floor) => ({
        id: floor.id,
        name: floor.name,
        totals: sumOccupancyTotals(floor.buildingStatisticOffices ?? []),
      }));

      return {
        id: building.id,
        name: building.name,
        city: city.name,
        floors,
        totals: sumOccupancyTotals(floors.map((floor) => floor.totals)),
      };
    }),
  );

  return { buildings, totals: sumOccupancyTotals(buildings.map((building) => building.totals)) };
};
